import { Button, Modal, Text } from '@ksyos/design-system';
import type { ValidationErrors } from '../../../utils/validation';
import { YesOrNo } from '../../../../common/enums/yesOrNo';
import { evaluationRequestItemSchema } from '../../../../common/validation/evaluation';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { Component } from '../../elements/Component';

interface IEvaluationRequestItemProps {
    handleChange?: <K extends keyof IEvaluation>(name: K, value: IEvaluation[K]) => Promise<void>;
    onClose: () => Promise<void>;
    requestId?: string;
}

interface IEvaluationRequestItemState {
    errors: ValidationErrors;
    evaluation: IEvaluation;
    isSaving: boolean;
}

export interface IEvaluation {
    otherwiseReferPatient: YesOrNo;
}

export class EvaluationRequestItem extends Component<IEvaluationRequestItemProps, IEvaluationRequestItemState> {
    constructor(props: IEvaluationRequestItemProps, context?: {}) {
        super(props, context);
        this.state = {
            isSaving: false,
            errors: emptyValidationErrors,
            evaluation: {
                otherwiseReferPatient: YesOrNo.no,
            },
        };
    }

    public render() {
        return (
            <>
                <Modal
                    closeOnEsc={false}
                    isOpen={true}
                    onClose={this.handleClose}
                    size="small"
                >
                    <Modal.Header
                        className="automation-otherwiseReferPatientTitle"
                        data-automation-test="otherwiseReferPatientTitle"
                    >
                        {translateKey('evaluation.otherwiseReferPatientTitle')}
                    </Modal.Header>
                    <Modal.Body>
                        <Text>{translateKey('evaluation.otherwiseReferPatient')}</Text>
                    </Modal.Body>
                    <Modal.Footer className="automation-modal-footer">
                        <Button
                            className="automation-otherwiseReferPatientTitleButton"
                            data-automation-test="otherwiseReferPatientTitleButton"
                            isDisabled={this.state.isSaving}
                            onClick={async () => {
                                await this.handleChange('otherwiseReferPatient', YesOrNo.no);
                                await this.handleClose();
                            }}
                            variant="outline"
                        >
                            {translateKey('common.no')}
                        </Button>
                        <Button
                            className="automation-otherwiseReferPatientTitleButton"
                            isDisabled={this.state.isSaving}
                            onClick={async () => {
                                await this.handleChange('otherwiseReferPatient', YesOrNo.yes);
                                await this.handleClose();
                            }}
                            variant="outline"
                        >
                            {translateKey('common.yes')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }

    private async handleChange<K extends keyof IEvaluation>(name: K, value: IEvaluation[K]) {
        if (this.props.handleChange) {
            await this.props.handleChange(name, value);
        } else {
            await this.setState(({ evaluation }) => ({ evaluation: { ...evaluation, [name]: value } }));
        }
    }

    private async handleClose() {
        if (this.props.requestId) {
            await this.save();
        }

        await this.props.onClose();
    }

    private async save() {
        const url = '/api/evaluations/saveEvaluationRequestItem';

        const { errors, item } = validate(evaluationRequestItemSchema, {
            ...this.state.evaluation,
            requestId: this.props.requestId,
        });
        await this.setState({ errors });
        if (errors.size > 0) {
            return;
        }
        await this.setState({ isSaving: true });
        await fetchJson(url, 'POST', item);
    }
}
